import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '@/context/AuthContext';
import { Shield, ArrowLeft, Loader2, CheckCircle2, AlertCircle, Copy } from 'lucide-react';

export default function AdminRecoveryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState('request'); // request | reset | done
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [devToken, setDevToken] = useState(''); // exposed only in the demo response

  // If the user arrived via an email link (?token=...), jump straight to the reset step
  useEffect(() => {
    const t = searchParams.get('token');
    if (t) {
      setToken(t);
      setStep('reset');
    }
  }, [searchParams]);

  const submitRequest = async (e) => {
    e.preventDefault();
    setErr(''); setMsg(''); setDevToken('');
    setBusy(true);
    try {
      const res = await axios.post(`${API_BASE}/auth/admin-recovery/request`, { email: email.trim() });
      setMsg(res.data.message);
      if (res.data.recovery_token_preview) {
        setDevToken(res.data.recovery_token_preview);
        setToken(res.data.recovery_token_preview);
      }
      setStep('reset');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to request reset.');
    } finally { setBusy(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setErr('');
    if (newPwd !== confirmPwd) { setErr('New Password and Confirm Password do not match.'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API_BASE}/auth/admin-recovery/reset`, {
        token: token.trim(),
        new_password: newPwd,
        confirm_password: confirmPwd,
      });
      setMsg(res.data.message);
      setStep('done');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to reset password.');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4 text-slate-100">
      <div className="w-full max-w-md bg-slate-900/70 border border-slate-800 rounded-2xl p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 flex items-center justify-center">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <h1 data-testid="admin-recovery-title" className="text-xl font-bold text-white">Administrator Recovery</h1>
            <p className="text-xs text-slate-400 mt-0.5">Reset the primary admin password using the verified recovery email.</p>
          </div>
        </div>

        {step === 'request' && (
          <form onSubmit={submitRequest} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">Recovery Email</label>
              <input
                data-testid="admin-recovery-email-input"
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Registered recovery email"
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-amber-500"
              />
            </div>
            {err && (
              <div data-testid="recovery-error" className="p-2.5 bg-red-950/60 border border-red-800/70 rounded-xl text-red-200 text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="admin-recovery-request-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-950 font-semibold text-sm rounded-xl"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Send Recovery Link
            </button>
            <button type="button" onClick={() => navigate('/login')} className="w-full text-xs text-slate-400 hover:text-white flex items-center justify-center gap-1">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Login
            </button>
          </form>
        )}

        {step === 'reset' && (
          <form onSubmit={submitReset} className="space-y-4">
            {msg && (
              <div className="p-2.5 bg-emerald-950/60 border border-emerald-800/60 rounded-xl text-emerald-200 text-xs flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{msg}</span>
              </div>
            )}
            {devToken && (
              <div data-testid="dev-token-banner" className="p-2.5 bg-slate-950/70 border border-slate-700 rounded-xl text-slate-300 text-[11px] leading-relaxed">
                <div className="font-semibold text-amber-300 mb-1">Demo mode</div>
                Recovery token shown here for testing (in production this would arrive via email). Token:
                <div className="font-mono text-xs text-white break-all mt-1 flex items-start gap-1">
                  <span>{devToken}</span>
                  <button type="button" onClick={() => navigator.clipboard.writeText(devToken)} className="text-teal-400 hover:text-teal-200"><Copy className="w-3 h-3" /></button>
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">Recovery Token</label>
              <input
                data-testid="recovery-token-input"
                type="text"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Paste the token from your recovery email"
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white font-mono focus:outline-none focus:border-amber-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">New Password</label>
              <input data-testid="recovery-new-password" type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">Confirm Password</label>
              <input data-testid="recovery-confirm-password" type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-amber-500" />
            </div>
            {err && (
              <div data-testid="recovery-error" className="p-2.5 bg-red-950/60 border border-red-800/70 rounded-xl text-red-200 text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {err}
              </div>
            )}
            <button type="submit" disabled={busy} data-testid="admin-recovery-reset-btn" className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-950 font-semibold text-sm rounded-xl">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Reset Admin Password
            </button>
          </form>
        )}

        {step === 'done' && (
          <div className="space-y-4 text-center py-4">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />
            <p data-testid="recovery-done-msg" className="text-sm text-slate-200">{msg}</p>
            <button onClick={() => navigate('/login')} className="inline-flex items-center gap-1.5 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-slate-950 text-sm font-semibold rounded-xl">
              Continue to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
